import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Heart } from "lucide-react";

export type PetEmotion = "happy" | "excited" | "sleepy" | "curious" | "sad" | "love";

interface PetAvatarProps {
  avatarUrl: string | null | undefined;
  mode: string;
  size: "sm" | "md" | "lg" | "xl";
  className?: string;
  state?: string;
  isMemorial?: boolean;
  emotion?: PetEmotion;
}

const SIZE_CLASSES: Record<string, string> = {
  sm: "w-8 h-8 text-sm",
  md: "w-10 h-10 text-lg",
  lg: "w-16 h-16 text-3xl",
  xl: "w-24 h-24 text-5xl",
};

const RADIUS_CLASSES: Record<string, string> = {
  sm: "rounded-full",
  md: "rounded-full",
  lg: "rounded-[18px]",
  xl: "rounded-[24px]",
};

const EmotionOverlay: React.FC<{ emotion: PetEmotion; size: string }> = ({ emotion, size }) => {
  const isSmall = size === "sm" || size === "md";

  switch (emotion) {
    case "love":
      return (
        <motion.div
          className="absolute -top-1 -right-1"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ repeat: Infinity, duration: 1.2 }}
        >
          <Heart className={cn("text-pink-400 fill-pink-400", isSmall ? "w-3 h-3" : "w-4 h-4")} />
        </motion.div>
      );
    case "sleepy":
      return (
        <motion.div
          className={cn("absolute text-xs opacity-70", isSmall ? "-top-1 right-0" : "-top-2 right-0")}
          animate={{ y: [0, -4, 0], opacity: [0.5, 0.8, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          💤
        </motion.div>
      );
    case "excited":
      return (
        <motion.div
          className={cn("absolute -top-1 -right-1 font-bold", isSmall ? "text-[10px]" : "text-xs")}
          animate={{ scale: [1, 1.4, 1] }}
          transition={{ repeat: Infinity, duration: 0.6 }}
        >
          <span className="text-amber-400">!</span>
        </motion.div>
      );
    case "curious":
      return (
        <motion.div
          className={cn("absolute", isSmall ? "-top-1 right-0 text-xs" : "-top-2 right-0 text-sm")}
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <span className="text-amber-400">?</span>
        </motion.div>
      );
    case "sad":
      return (
        <div className="absolute inset-0 rounded-[inherit] bg-[hsla(220,40%,70%,0.15)] pointer-events-none" />
      );
    default:
      return null; // happy = no overlay
  }
};

export const PetAvatar: React.FC<PetAvatarProps> = ({
  avatarUrl,
  mode,
  size,
  className,
  state,
  isMemorial,
  emotion,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const memorial = isMemorial ?? mode === "memorial";
  const fallbackEmoji = memorial ? "✨" : "🐕";
  const sizeClass = SIZE_CLASSES[size];
  const radiusClass = RADIUS_CLASSES[size];

  // Map state to emotion if emotion not explicitly provided
  const resolvedEmotion: PetEmotion | undefined = emotion || (state ? stateToEmotion(state) : undefined);

  if (!avatarUrl || error) {
    return (
      <div
        className={cn(
          `${sizeClass} ${radiusClass} flex items-center justify-center shrink-0 relative`,
          memorial
            ? "bg-gradient-to-br from-[hsla(230,45%,88%,1)] to-[hsla(260,35%,82%,0.6)]"
            : "bg-gradient-to-br from-warm-peach to-warm-orange/40",
          className
        )}
      >
        {fallbackEmoji}
        {resolvedEmotion && resolvedEmotion !== "happy" && (
          <EmotionOverlay emotion={resolvedEmotion} size={size} />
        )}
      </div>
    );
  }

  return (
    <div className={cn(`relative ${sizeClass} ${radiusClass} shrink-0 overflow-visible`, className)}>
      <div className={cn(`w-full h-full ${radiusClass} overflow-hidden`)}>
        {/* Placeholder while loading */}
        {!loaded && (
          <div
            className={cn(
              `absolute inset-0 ${radiusClass} flex items-center justify-center animate-pulse`,
              memorial
                ? "bg-[hsla(230,40%,90%,0.5)]"
                : "bg-warm-peach/20"
            )}
          >
            {fallbackEmoji}
          </div>
        )}
        <img
          src={avatarUrl}
          alt="Pet avatar"
          loading="lazy"
          decoding="async"
          className={cn(
            `w-full h-full object-cover ${radiusClass} transition-opacity duration-300`,
            loaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
        {/* Memorial overlay */}
        {memorial && loaded && (
          <div className={`absolute inset-0 ${radiusClass} bg-[hsla(250,30%,70%,0.08)] pointer-events-none`} />
        )}
      </div>

      {/* Emotion overlay — outside clip area */}
      <AnimatePresence>
        {resolvedEmotion && resolvedEmotion !== "happy" && loaded && (
          <EmotionOverlay emotion={resolvedEmotion} size={size} />
        )}
      </AnimatePresence>

      {/* Memorial glow */}
      {memorial && loaded && (
        <div
          className={`absolute inset-0 ${radiusClass} pointer-events-none`}
          style={{ boxShadow: "0 0 16px 4px hsla(260, 40%, 80%, 0.25)" }}
        />
      )}
    </div>
  );
};

function stateToEmotion(state: string): PetEmotion | undefined {
  const map: Record<string, PetEmotion> = {
    excited: "excited",
    happy: "happy",
    playful: "excited",
    sleepy: "sleepy",
    curious: "curious",
    sad: "sad",
    nostalgic: "sad",
    love: "love",
    warm: "love",
    mischievous: "excited",
    grateful: "love",
  };
  return map[state];
}
